import { Logger, SymbolType } from "@spawn/common";
import { inject, view } from "@spawn/core";
import { DEFAULT_LAYER_PRIORITY, GoldenBonusMetadataKeys } from "@spawn/golden-bonus";
import { enforceMysteryBlocks, RevealReelView, RevealService } from "@spawn/reveal";
import { SpinPhase, SymbolView, SymbolViewEvent } from "@spawn/slot-machine";
import { first, skip } from "rxjs";
import { NrgLaglessGenerator } from "../lagless/customLaglessGenerator";

@view
export class NrgReelView extends RevealReelView {
	@inject protected readonly laglessGenerator!: NrgLaglessGenerator;
	@inject protected readonly revealService!: RevealService;

	/** A resting scatter draws over the other symbols, as it does on the spotlight. */
	protected override getLayerPriority(symbolView: SymbolView): number {
		return (
			symbolView.symbolModel.getMetaData<number | undefined>(GoldenBonusMetadataKeys.LayerPriority) ?? DEFAULT_LAYER_PRIORITY
		);
	}

	// Override reason: method is mainly copied, but we need to add the mystery block size check
	public override setTargetSymbols(symbols: SymbolType[], paddingSymbolCount: number): void {
		Logger.info(`ReelView: ${this.reelIndex} Target symbols: ${symbols} Padding symbol count: ${paddingSymbolCount}`);
		this.spinStartTime = performance.now();
		const { reelIndex, addons } = this.config;

		void this.fireEventOnSymbols(SymbolViewEvent.OnReelStart);
		// override: the strip must never run into a part of a mystery block
		const laglessStrip = enforceMysteryBlocks(this.laglessGenerator.getLaglessSymbols(paddingSymbolCount, reelIndex), symbols, {
			mysterySymbol: this.revealService.mysterySymbol(),
			blockSize: this.revealService.mysteryBlockSize(),
			getFillerSymbol: () => this.laglessGenerator.getRandomNonCriticalSymbols(1)[0],
		});

		this.symbols = [...laglessStrip, ...[...symbols].reverse()];

		for (const addon of addons) {
			addon.onReelStart?.(this);
		}
		// Set the first symbol
		if (this.reelPhase$.value === SpinPhase.LagLessSpinning) {
			this.reelPhase$.next(SpinPhase.Spinning);
			return;
		}

		if (this.reelPhase$.value === SpinPhase.WaitingToStart) {
			// eslint-disable-next-line @spawn/unmanaged-rxjs-subscription
			this.reelPhase$.pipe(skip(1), first()).subscribe(() => {
				this.reelPhase$.next(SpinPhase.Spinning);
			});
		}
	}
}
